import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, ArrowLeft } from "lucide-react";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Reviews } from "@/components/Reviews";
import { Stars } from "@/components/Stars";
import { WishlistButton } from "@/components/WishlistButton";
import { CustomizePanel, summaryOf } from "@/components/CustomizePanel";

const Options = ({ label, values, value, onChange, testid }) => values?.length > 0 && (
  <div>
    <p className="font-mono text-xs uppercase tracking-widest text-neutral-400 mb-3">{label}: <span className="text-white">{value}</span></p>
    <div className="flex flex-wrap gap-2">
      {values.map((v) => (
        <button key={v} data-testid={`${testid}-${v.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => onChange(v)}
          className={`min-w-12 px-4 h-11 font-mono text-sm border transition-colors ${value === v ? "bg-white text-black border-white" : "border-white/15 text-neutral-300 hover:border-white/50 hover:text-white"}`}>
          {v}
        </button>
      ))}
    </div>
  </div>
);

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [custom, setCustom] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      const d = r.data;
      setP(d); setSize(d.sizes?.[0]); setColor(d.colors?.[0]);
      setCustom(d.pod ? { color: d.pod.colors[0].name, size: d.pod.sizes[0], placement: d.pod.placements[0].key } : null);
    }).catch(() => setP(false));
  }, [id]);

  if (p === false) return <p className="text-center py-32 text-neutral-400">Product not found.</p>;
  if (!p) return <div className="max-w-7xl mx-auto px-8 py-12"><div className="aspect-[4/3] bg-white/5 animate-pulse" /></div>;
  const variant = p.variants?.find((v) => v.name === size);
  const price = variant?.price ?? p.price;
  const addToCart = () => {
    if (!p.pod) return add(p, { size, color, quantity: qty });
    const pl = p.pod.placements.find((x) => x.key === custom.placement);
    add(p, { size: custom.size, color: custom.color, quantity: qty,
      customization: { placement: custom.placement, placement_label: pl.label, summary: summaryOf(p.pod, custom) } });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
      <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-neutral-400 hover:text-white mb-8"><ArrowLeft className="w-4 h-4" />Back to shop</Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="relative border border-white/10 bg-neutral-900 aspect-[4/5] overflow-hidden rise">
          <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
          <span className="absolute bottom-4 left-4 bg-[#FF3B30] -skew-x-12 px-3 py-1 font-display font-black text-white">ABS</span>
        </div>
        <div className="rise" style={{ animationDelay: "120ms" }}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3B30] font-semibold">{p.category}</p>
          <h1 data-testid="product-title" className="font-display font-black uppercase tracking-tight leading-none text-4xl sm:text-5xl mt-3">{p.name}</h1>
          <p data-testid="product-detail-price" className="font-mono text-2xl mt-5">{money(price)}{variant?.servings && <span className="text-sm text-neutral-400 ml-3" data-testid="variant-servings">{variant.servings} servings</span>}</p>
          {p.rating_count > 0 && (
            <a href="#reviews" data-testid="product-detail-rating" className="inline-flex items-center gap-2 mt-3 hover:opacity-80"><Stars value={p.rating_avg} size="w-4 h-4" /><span className="font-mono text-xs text-neutral-400">{p.rating_avg.toFixed(1)} · {p.rating_count} review{p.rating_count === 1 ? "" : "s"}</span></a>
          )}
          {p.is_digital ? (
            <p data-testid="digital-badge" className="font-mono text-xs mt-2 text-emerald-400">● DIGITAL PRODUCT · INSTANT ACCESS · NO SHIPPING</p>
          ) : (
            <p data-testid="stock-status" className={`font-mono text-xs mt-2 ${p.stock > 5 ? "text-emerald-400" : p.stock > 0 ? "text-[#FF9500]" : "text-[#FF3B30]"}`}>
              {p.stock > 5 ? "● IN STOCK" : p.stock > 0 ? `● ONLY ${p.stock} LEFT` : "● SOLD OUT"}
            </p>
          )}
          <p className="text-neutral-300 leading-relaxed mt-6">{p.description}</p>
          {p.pod && custom ? (
            <CustomizePanel pod={p.pod} value={custom} onChange={setCustom} />
          ) : (
            <div className="space-y-6 mt-8">
              <Options label={p.is_digital ? "Format" : "Size"} values={p.sizes} value={size} onChange={setSize} testid="size-option" />
              <Options label="Color" values={p.colors} value={color} onChange={setColor} testid="color-option" />
            </div>
          )}
          <div className="flex gap-3 mt-10">
            <div className="flex items-center border border-white/15 h-14">
              <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-full grid place-items-center hover:bg-white/10 hover:text-white"><Minus className="w-4 h-4" /></button>
              <span data-testid="qty-value" className="w-10 text-center font-mono">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty(Math.min(p.stock, qty + 1))} className="w-12 h-full grid place-items-center hover:bg-white/10 hover:text-white"><Plus className="w-4 h-4" /></button>
            </div>
            <button data-testid="add-to-cart-button" disabled={p.stock <= 0} onClick={addToCart}
              className="flex-1 h-14 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-black tracking-wider text-xl transition-colors disabled:opacity-40">
              Add to cart
            </button>
            <WishlistButton productId={p.id} size="w-5 h-5" className="w-14 h-14 border border-white/15 hover:border-[#FF3B30]" />
          </div>
          {p.specs && (
            <div className="mt-10 border-t border-white/10 pt-6">
              <p className="font-mono text-xs uppercase tracking-widest text-neutral-500 mb-2">Specs</p>
              <p className="font-mono text-sm text-neutral-300">{p.specs}</p>
            </div>
          )}
        </div>
      </div>
      <div id="reviews"><Reviews productId={p.id} /></div>
    </div>
  );
}
